import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { Subscription } from 'rxjs';
import { OrgFeature } from '@newgenus/common';
import { OrgWizardStateService } from '../services/org-wizard-state.service';

/**
 * Step 5 — Plan & Features.
 *
 * Plan reference: §4.3 Step 5, §16.4.
 *
 * P1 scope:
 *  - Loads the global `features` collection (the same source the orchestrator
 *    copies from in stage 2 — kept consistent so what the user sees here is
 *    exactly what will be written to the new org).
 *  - Groups features by their first `featureGroups[]` entry into bundles.
 *    System features (`systemFeature === true`) are bundled separately under
 *    "System" and **always selected, never togglable** — without them the
 *    org-board / settings / users pages don't function.
 *  - Bundle-level toggles: a single click selects/deselects the whole bundle.
 *  - "Advanced ▾" disclosure under each bundle reveals individual feature
 *    checkboxes for power users.
 *  - Counter: `<selected> / <total>` features at the top.
 *  - Default selection on first visit = every non-system feature (mirrors the
 *    pre-step-5 behaviour so downgrading from "all" to "less" is an explicit
 *    choice the user makes, never accidental).
 *
 * The wizard state stores the result as `OrgDraft.features: string[]` (sorted,
 * de-duplicated). The backend orchestrator (stage 2) reads this and copies
 * only the matching documents from the global `features` collection into the
 * new org's `organizations/{orgKey}/features` subcollection.
 */
@Component({
    selector: 'newgenus-org-creation-step-features',
    templateUrl: './step-5-features.component.html',
    styleUrls: ['./step-5-features.component.scss'],
    standalone: false,
})
export class StepFeaturesComponent implements OnInit, OnDestroy {
    private db = inject(AngularFirestore);
    public state = inject(OrgWizardStateService);

    public loading = true;
    public loadError: string | null = null;

    /** All features available to copy into the new org. */
    public allFeatures: OrgFeature[] = [];

    /** Bundles derived from `featureGroups[0]`. System features sit in their own bundle. */
    public bundles: FeatureBundle[] = [];

    /** Currently-selected feature keys (mirror of `state.snapshot.features`). */
    public selected = new Set<string>();

    /** Disclosure state: which bundles have their advanced view open. */
    public expanded = new Set<string>();

    private sub = new Subscription();

    public ngOnInit(): void {
        this.sub.add(
            this.db
                .collection<OrgFeature>('features')
                .get()
                .subscribe({
                    next: (snap) => {
                        this.allFeatures = snap.docs.map((d) => ({ ...(d.data() as OrgFeature), key: d.id }));
                        this.bundles = this.buildBundles(this.allFeatures);

                        // Seed selection from the draft if we have one; otherwise
                        // default to every non-system feature plus the always-on
                        // system bundle (system bundle is non-togglable so the
                        // backend orchestrator still gets it for free via the
                        // selection union below).
                        const draftFeatures = this.state.snapshot?.features ?? [];
                        if (draftFeatures.length > 0) {
                            this.selected = new Set(draftFeatures);
                            // Make sure system features are always present even
                            // if an older draft was saved without them.
                            for (const f of this.allFeatures) {
                                if (f.systemFeature) this.selected.add(f.key);
                            }
                        } else {
                            this.selected = new Set(this.allFeatures.map((f) => f.key));
                        }

                        // Push the seeded selection back into the draft once so
                        // navigating away without touching anything still saves
                        // the selection (no undo entry — this is initialisation,
                        // not a user action).
                        this.state.setFeatures([...this.selected], { pushHistory: false });

                        this.loading = false;
                    },
                    error: (err: unknown) => {
                        console.error('StepFeaturesComponent > load features error:', err);
                        this.loadError = 'Could not load features. You can continue and a default set will be used.';
                        this.loading = false;
                    },
                })
        );
    }

    public ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    public isBundleSelected(bundle: FeatureBundle): 'all' | 'some' | 'none' {
        const featureKeys = bundle.features.map((f) => f.key);
        const selectedCount = featureKeys.filter((k) => this.selected.has(k)).length;
        if (selectedCount === 0) return 'none';
        if (selectedCount === featureKeys.length) return 'all';
        return 'some';
    }

    public toggleBundle(bundle: FeatureBundle): void {
        if (bundle.locked) return;
        const featureKeys = bundle.features.map((f) => f.key);
        const state = this.isBundleSelected(bundle);
        if (state === 'all') {
            for (const k of featureKeys) this.selected.delete(k);
        } else {
            for (const k of featureKeys) this.selected.add(k);
        }
        this.commit();
    }

    public toggleFeature(featureKey: string, locked = false): void {
        if (locked) return;
        if (this.selected.has(featureKey)) this.selected.delete(featureKey);
        else this.selected.add(featureKey);
        this.commit();
    }

    public toggleExpanded(bundle: FeatureBundle): void {
        if (this.expanded.has(bundle.id)) this.expanded.delete(bundle.id);
        else this.expanded.add(bundle.id);
    }

    public selectAll(): void {
        this.selected = new Set(this.allFeatures.map((f) => f.key));
        this.commit();
    }

    public selectMinimum(): void {
        // System features only — the absolute minimum the new org needs to be navigable.
        this.selected = new Set(this.allFeatures.filter((f) => f.systemFeature).map((f) => f.key));
        this.commit();
    }

    public get totalCount(): number {
        return this.allFeatures.length;
    }

    public get selectedCount(): number {
        return this.selected.size;
    }

    private commit(): void {
        this.state.setFeatures([...this.selected]);
    }

    /**
     * Group features by their first `featureGroups` entry (lowercased + title-cased
     * for display). Features without a group land in "Other". `systemFeature`
     * features are pulled out into a dedicated, locked "System" bundle.
     */
    private buildBundles(features: OrgFeature[]): FeatureBundle[] {
        const systemBundle: FeatureBundle = {
            id: '__system__',
            name: 'System (always on)',
            description: 'Required core features — every organization needs these to function.',
            features: [],
            locked: true,
        };

        const byGroup = new Map<string, FeatureBundle>();
        for (const f of features) {
            if (f.systemFeature) {
                systemBundle.features.push(f);
                continue;
            }
            const groupKey = (f.featureGroups?.[0] ?? 'Other').trim() || 'Other';
            if (!byGroup.has(groupKey)) {
                byGroup.set(groupKey, {
                    id: groupKey,
                    name: this.titleCase(groupKey),
                    description: '',
                    features: [],
                    locked: false,
                });
            }
            byGroup.get(groupKey)!.features.push(f);
        }

        const ordered = Array.from(byGroup.values()).sort((a, b) => a.name.localeCompare(b.name));
        return systemBundle.features.length ? [systemBundle, ...ordered] : ordered;
    }

    private titleCase(s: string): string {
        return s
            .replace(/[-_]+/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase())
            .trim();
    }
}

/** A grouping of features the user selects/deselects together. */
export interface FeatureBundle {
    id: string;
    name: string;
    description: string;
    features: OrgFeature[];
    /** When `true`, the bundle is non-togglable (used for the System bundle). */
    locked: boolean;
}
